import { Fragment, useRef } from 'react';

import { Carousel } from '@xing-com/carousel';

import { MetaLanguageMultiPage } from '../consumers/prescreen-consumer/meta-language-multi-page';
import { MetaLanguageSinglePage } from '../consumers/prescreen-consumer/meta-language-single-page';
import type { BrazeCustomEvent } from '../consumers/prescreen-consumer/prescreen-consumer';
import type { MetaLanguage } from '../utils/custom-meta-language';
import type { HtmlSerializerProps } from '../utils/html-serializer';
import htmlSerializer from '../utils/html-serializer';

interface MetaLanguageRendererProps {
  metaLanguage: MetaLanguage;
  onClose: () => void;
  onButtonClick?: (buttonId: string) => void;
  onURLButtonClick: (
    url: string,
    target?: '_self' | '_blank',
    buttonId?: string
  ) => void;
  buttonColor?: HtmlSerializerProps['buttonColor'];
  sendBrazeCustomEvent: (payload: BrazeCustomEvent) => void;
}

export const MetaLanguageRenderer: React.FC<MetaLanguageRendererProps> = ({
  metaLanguage,
  onClose,
  onButtonClick,
  onURLButtonClick,
  buttonColor,
  sendBrazeCustomEvent,
}) => {
  const metaLanguageRef = useRef<HTMLDivElement>(null);

  switch (metaLanguage.type) {
    case 'custom':
      return (
        <MetaLanguageSinglePage
          content={metaLanguage.content}
          successContent={metaLanguage.successContent}
          onClose={onClose}
          onButtonClick={onButtonClick}
          onURLButtonClick={onURLButtonClick}
          buttonColor={buttonColor}
          sendBrazeCustomEvent={sendBrazeCustomEvent}
        />
      );
    case 'custom-multi-page':
      return (
        <div ref={metaLanguageRef}>
          <MetaLanguageMultiPage
            content={metaLanguage.content}
            fallback={metaLanguage.fallback}
            popupRef={metaLanguageRef}
            onClose={onClose}
            onButtonClick={onButtonClick}
            onURLButtonClick={onURLButtonClick}
            sendBrazeCustomEvent={sendBrazeCustomEvent}
          />
        </div>
      );
    case 'carousel':
      return (
        <Carousel
          {...(metaLanguage.title ? { 'aria-label': metaLanguage.title } : {})}
          hideScrollbar
          itemWidthConfined="100%"
          itemSnapPosition="center"
          tabbable={true}
        >
          {metaLanguage.content.map((card, cardIndex) => (
            <div key={`card-${cardIndex}`}>
              {card.map((content, contentIndex) => (
                <Fragment key={`card-${cardIndex}-content-${contentIndex}`}>
                  {htmlSerializer({
                    content,
                    onClose,
                    onButtonClick,
                    onURLButtonClick,
                    buttonColor,
                    sendBrazeCustomEvent,
                  })}
                </Fragment>
              ))}
            </div>
          ))}
        </Carousel>
      );
  }
};
